import { useEffect, useState } from 'react';

type FeaturePreviewLightboxProps = {
  image: string;
  alt: string;
  className?: string;
};

/** Opens a product screenshot without taking visitors away from its feature story. */
export default function FeaturePreviewLightbox({ image, alt, className }: FeaturePreviewLightboxProps) {
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (!open) return;

    const previousOverflow = document.body.style.overflow;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(false);
    };

    document.body.style.overflow = 'hidden';
    window.addEventListener('keydown', onKeyDown);
    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener('keydown', onKeyDown);
    };
  }, [open]);

  return (
    <>
      <button
        type="button"
        className={className}
        aria-label={`Open full-size preview: ${alt}`}
        aria-haspopup="dialog"
        onClick={() => setOpen(true)}
      >
        <img src={image} alt={alt} loading="lazy" />
      </button>

      {open ? (
        <div
          className="feature-preview-lightbox"
          role="presentation"
          onClick={() => setOpen(false)}
        >
          <div
            className="feature-preview-lightbox__dialog"
            role="dialog"
            aria-modal="true"
            aria-label={alt}
            onClick={(event) => event.stopPropagation()}
          >
            <button
              type="button"
              className="feature-preview-lightbox__close"
              aria-label="Close full-size preview"
              onClick={() => setOpen(false)}
            >
              x
            </button>
            <img src={image} alt={alt} />
          </div>
        </div>
      ) : null}
    </>
  );
}
